import { useState } from "react";
import { useAddNote, type Note } from "@/app/reviewQueries";
import { Button } from "@/ui/Button";
import { fmtDateTime, sortNotes } from "./model";

/** Приватные заметки менторов: видят менторы и админы школы, студент — нет. */
export function Notes({ userId, notes }: { userId: string; notes: Note[] }) {
  const [draft, setDraft] = useState("");
  const add = useAddNote(userId);
  const text = draft.trim();
  const submit = async () => {
    if (!text) return;
    await add.mutateAsync(text);
    setDraft("");
  };
  const errMsg = add.error instanceof Error ? add.error.message : add.error ? "Не удалось сохранить заметку." : null;
  return (
    <section data-testid="notes">
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <h2 className="m-0 font-mono text-[16px] font-bold tracking-[-.01em]">Заметки ментора</h2>
        <span className="font-mono text-[11.5px] text-mut">приватные заметки — видят только менторы</span>
      </div>
      <form
        className="mb-4 flex flex-col gap-2.5 rounded-md border border-bd bg-card p-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[11px] tracking-[.04em] text-mut">новая заметка</span>
          <textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="что важно помнить про этого студента"
            className="resize-y rounded-sm border border-bd2 bg-bg2 px-[11px] py-[9px] text-[13px] leading-[1.5] text-ink outline-none placeholder:text-mut focus:border-acc"
            data-testid="note-input"
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" size="sm" disabled={!text || add.isPending} reason={!text ? "напишите текст заметки" : add.isPending ? "сохраняем…" : undefined} data-testid="note-submit">
            добавить заметку
          </Button>
          {errMsg && (
            <span role="alert" className="font-mono text-[11.5px] text-err" data-testid="note-error">
              {errMsg}
            </span>
          )}
        </div>
      </form>
      {notes.length === 0 ? (
        <p className="m-0 rounded-md border border-dashed border-bd px-4 py-5 font-mono text-[12.5px] text-mut" data-testid="notes-empty">
          Заметок пока нет. Первая будет видна всем менторам школы — студенту нет.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0" data-testid="notes-list">
          {sortNotes(notes).map((n) => (
            <li key={n.id} data-testid="note" className="rounded-md border border-bd bg-card px-3.5 py-3" style={{ borderLeft: "2px solid var(--bd2)" }}>
              <div className="mb-1.5 font-mono text-[11px] text-mut">
                <span className="text-ink2">{n.author.name}</span> · {fmtDateTime(n.created_at)}
              </div>
              <p className="m-0 text-[13px] leading-[1.55] text-ink">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
